import React, { useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserProfile } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { useLicenseBanner } from '../context/LicenseBannerContext';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '../hooks/useTranslation';
import { ROLES } from '../utils/constants';
import LicenseBellIcon from '../assets/icons/license-bell.svg';
import { useToaster } from '../context/ToasterContext';

const LicenseExpiryBanner = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const { data: profileData, isLoading } = useUserProfile({
    enabled: isAuthenticated() && user?.role !== ROLES.SUPERADMIN
  });
  const { t, isRTL } = useTranslation();
  const { setIsVisible, setBannerHeight } = useLicenseBanner();
  const { showWarning } = useToaster();
  const [isDismissed, setIsDismissed] = React.useState(false);
  const bannerRef = React.useRef(null);

  // Check if license is expired and redirect to login
  useEffect(() => {
    // Don't check for superadmin
    if (user?.role === ROLES.SUPERADMIN) {
      return;
    }

    // Don't check if profile is still loading or not available
    if (isLoading || !profileData?.result) {
      return;
    }

    const licenseExpiryDate = profileData.result.company?.licenseExpiryDate;
    if (!licenseExpiryDate) {
      return;
    }

    try {
      // Parse the expiry date
      const expiryDate = new Date(licenseExpiryDate);
      if (isNaN(expiryDate.getTime())) {
        return;
      }

      // Get current date (set to start of day for accurate comparison)
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Set expiry date to start of day
      const expiry = new Date(expiryDate);
      expiry.setHours(0, 0, 0, 0);

      // Check if license has expired (expiry date is before today)
      if (expiry < today) {
        // License has expired, logout and redirect to login
        showWarning(t('auth.licenseExpiredCap'), t('auth.licenseExpiredContactSupport'));
        logout();
      }
    } catch (error) {
      console.error('Error checking license expiry:', error);
    }
  }, [profileData, isLoading, user, logout, showWarning]);

  // Check if license expires in 30 days or less
  const shouldShowBanner = useMemo(() => {
    // Don't show for superadmin or if not authenticated
    if (!isAuthenticated() || user?.role === ROLES.SUPERADMIN) {
      return false;
    }

    if (isDismissed || isLoading || !profileData?.result) {
      return false;
    }

    const licenseExpiryDate = profileData.result.company?.licenseExpiryDate;
    if (!licenseExpiryDate) {
      return false;
    }

    try {
      // Parse the expiry date
      const expiryDate = new Date(licenseExpiryDate);
      if (isNaN(expiryDate.getTime())) {
        return false;
      }

      // Get current date (set to start of day for accurate comparison)
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Set expiry date to start of day
      const expiry = new Date(expiryDate);
      expiry.setHours(0, 0, 0, 0);

      // Calculate difference in days
      const diffTime = expiry - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      // Show banner if license expires in 30 days or less and hasn't expired yet
      return diffDays <= 30 && diffDays >= 0;
    } catch (error) {
      console.error('Error checking license expiry:', error);
      return false;
    }
  }, [profileData, isLoading, isDismissed, isAuthenticated, user]);

  // Format expiry date for display
  const formattedExpiryDate = useMemo(() => {
    if (!profileData?.result?.company?.licenseExpiryDate) {
      return '';
    }

    try {
      const expiryDate = new Date(profileData.result.company.licenseExpiryDate);
      if (isNaN(expiryDate.getTime())) {
        return '';
      }

      // Format as DD/MM/YYYY
      const day = String(expiryDate.getDate()).padStart(2, '0');
      const month = String(expiryDate.getMonth() + 1).padStart(2, '0');
      const year = expiryDate.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return '';
    }
  }, [profileData]);

  // Calculate days until expiry
  const daysUntilExpiry = useMemo(() => {
    if (!profileData?.result?.company?.licenseExpiryDate) {
      return null;
    }

    try {
      const expiryDate = new Date(profileData.result.company.licenseExpiryDate);
      if (isNaN(expiryDate.getTime())) {
        return null;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const expiry = new Date(expiryDate);
      expiry.setHours(0, 0, 0, 0);

      const diffTime = expiry - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch (error) {
      return null;
    }
  }, [profileData]);

  // Get license status with default to APPROVED
  const licenseStatus = useMemo(() => {
    return profileData?.result?.company?.license?.status || "APPROVED";
  }, [profileData]);

  // Check sessionStorage on mount
  React.useEffect(() => {
    const dismissed = sessionStorage.getItem('license_expiry_banner_dismissed');
    if (dismissed === 'true') {
      setIsDismissed(true);
    }
  }, []);

  // Update banner visibility and height in context
  React.useEffect(() => {
    if (shouldShowBanner && bannerRef.current) {
      const height = bannerRef.current.offsetHeight;
      setIsVisible(true);
      setBannerHeight(height);
    } else {
      setIsVisible(false);
      setBannerHeight(0);
    }
  }, [shouldShowBanner, setIsVisible, setBannerHeight]);

  const handleUpdateNow = () => {
    navigate('/service-provider/profile?tab=business');
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    // Store dismissal in sessionStorage to persist for the session
    sessionStorage.setItem('license_expiry_banner_dismissed', 'true');
  };

  if (!shouldShowBanner) {
    return null;
  }

  // Determine banner background color based on license status
  const bannerBackgroundColor = licenseStatus === "REJECTED" ? '#EF4444' : '#FFB24D'; // Red for REJECTED, Orange for others

  return (
    <div ref={bannerRef} className="w-full fixed top-0 left-0 right-0 z-[70]" style={{ backgroundColor: bannerBackgroundColor }}>
      <div className="w-full px-4 sm:px-6 lg:px-8 py-2">
        <div className={`flex flex-col items-center gap-2 sm:flex-row sm:justify-center sm:gap-4 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
          <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="flex-shrink-0">
              {/* <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" style={{ color: '#323232' }}>
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg> */}

              <img 
                src={LicenseBellIcon} 
                alt="License Bell" 
                className="w-5 h-5" 
                style={licenseStatus === "REJECTED" ? { 
                  filter: 'brightness(0) invert(1)',
                  opacity: 1
                } : {}}
              />
            </div>

            <div>
              {licenseStatus === "REJECTED" ? (
                <p className="text-sm font-medium" style={{ color: '#FFFFFF' }}>
                  {t('auth.licenseRejectedBannerMessage')}
                </p>
              ) : licenseStatus === "PENDING" ? (
                <p className="text-sm font-medium" style={{ color: '#323232' }}>
                  {t('auth.licenseUnderReviewBannerMessage')}
                </p>
              ) : (
                <p className="text-sm font-medium" style={{ color: '#323232' }}>
                  <b>{t('auth.licenseExpiringSoon')}</b> {t('auth.updateLicenseToAvoidRestriction')}
                </p>
              )}
            </div>
          </div>
          {(licenseStatus === "REJECTED" || (licenseStatus === "APPROVED" &&
    daysUntilExpiry !== null &&
    daysUntilExpiry <= 30)) && <div className={`flex w-full items-center justify-center gap-3 sm:w-auto ${isRTL ? 'flex-row-reverse' : ''}`}>
            <button
              onClick={handleUpdateNow}
              className="w-full px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap sm:w-auto sm:py-1"
              style={{
                backgroundColor: licenseStatus === "REJECTED" ? 'transparent' : 'transparent',
                border: licenseStatus === "REJECTED" ? '1px solid #FFFFFF' : '1px solid #323232',
                color: licenseStatus === "REJECTED" ? '#FFFFFF' : '#323232',
                borderRadius: '20px',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = licenseStatus === "REJECTED" ? 'rgba(255, 255, 255, 0.1)' : 'rgba(50, 50, 50, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              {t('auth.updateNow')}
            </button>
            {/* <button
              onClick={handleDismiss}
              className="hover:opacity-80 transition-opacity p-1"
              aria-label="Dismiss"
              style={{ color: '#323232' }}
            >
              <XMarkIcon className="w-5 h-5" />
            </button> */}
          </div>}
        </div>
      </div>
    </div>
  );
};

export default LicenseExpiryBanner;

